import React, { useEffect, useState } from 'react';
import './TimelineModal.css';

interface TimelineEvent {
  label: string;
  date: string;
  desc: string;
  status?: 'completed' | 'current' | 'upcoming';
}

interface TimelineModalProps {
  show: boolean;
  onClose: () => void;
  eventData: any;
  formatDate: (dateString: string) => string;
}

const TimelineModal: React.FC<TimelineModalProps> = ({
  show,
  onClose,
  eventData,
  formatDate
}) => {
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const getEventStatus = (eventDate: string): 'completed' | 'current' | 'upcoming' => {
    if (!eventDate) return 'upcoming';
    const today = new Date();
    const date = new Date(eventDate);
    if (date <= today) return 'completed';
    if (date.toDateString() === today.toDateString()) return 'current';
    return 'upcoming';
  };

  useEffect(() => {
    if (eventData?.event) {
      const event = eventData.event;
      const events: TimelineEvent[] = [];

      if (event.created_at) {
        events.push({
          label: "Event Created",
          date: event.created_at,
          desc: "Event was created in system.",
          status: 'completed'
        });
      }

      if (event.register_start_date) {
        events.push({
          label: "Registration Opens",
          date: event.register_start_date,
          desc: "Registration opens for participants.",
          status: getEventStatus(event.register_start_date)
        });
      }

      if (event.register_end_date) {
        events.push({
          label: "Registration Closes",
          date: event.register_end_date,
          desc: "Registration closes and voting period starts.",
          status: getEventStatus(event.register_end_date)
        });
      }

      if (event.vote_end_date) {
        events.push({
          label: "Voting Ends",
          date: event.vote_end_date,
          desc: "Voting period ends.",
          status: getEventStatus(event.vote_end_date)
        });
      }

      if (event.event_date) {
        events.push({
          label: "Event Day",
          date: event.event_date,
          desc: `Event takes place at ${event.location_name || 'TBD'}.`,
          status: getEventStatus(event.event_date)
        });
      }

      setTimelineEvents(events);
    }
  }, [eventData]);

  const calculateDaysBetween = (date1: string, date2: string): number => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'current': return '#dc3545';
      case 'upcoming': return '#6c757d';
      default: return '#0d6efd';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'completed': return 'bi-check-circle-fill';
      case 'current': return 'bi-clock-fill';
      case 'upcoming': return 'bi-circle';
      default: return 'bi-circle';
    }
  };

  if (!show) return null;

  return (
    <div className="timeline-modal-wrapper" onClick={onClose}>
      <div className="timeline-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header p-3 border-bottom">
          <h5 className="modal-title m-0">
            <i className="bi bi-clock-history me-2"></i>
            Timeline for {eventData?.event?.title || 'Event'}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
          ></button>
        </div>
        
        <div className="modal-body p-0">
          {isMobile ? (
            // Mobile layout - vertical list
            <div className="timeline-wrapper">
              {timelineEvents.map((event, index) => (
                <div 
                  key={index} 
                  className="timeline-mobile-event"
                  style={{ 
                    borderLeftColor: getStatusColor(event.status),
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <div 
                      className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                      style={{ 
                        width: '28px', 
                        height: '28px',
                        backgroundColor: getStatusColor(event.status),
                        color: 'white'
                      }}
                    >
                      <i className={`bi ${getStatusIcon(event.status)}`} style={{ fontSize: '0.8rem' }}></i>
                    </div>
                    <div>
                      <strong 
                        className="d-block" 
                        style={{ color: getStatusColor(event.status), fontSize: '0.95rem' }}
                      >
                        {event.label}
                      </strong>
                      <small className="text-muted">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(event.date)}
                      </small>
                    </div>
                  </div>
                  <div className="timeline-desc ps-4">
                    {event.desc}
                  </div>
                  <div className="mt-2 ps-4">
                    <span 
                      className="badge"
                      style={{ backgroundColor: getStatusColor(event.status), fontSize: '0.65rem' }}
                    >
                      {event.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop layout - horizontal timeline
            <div className="timeline-wrapper">
              <div className="timeline-line"></div>
              
              {timelineEvents.map((event, index) => {
                const leftPercentage = 15 + (index * 70 / Math.max(timelineEvents.length - 1, 1));
                const isAbove = index % 2 === 0;
                
                return (
                  <React.Fragment key={index}>
                    <div 
                      className="timeline-point"
                      style={{ 
                        left: `${leftPercentage}%`,
                        backgroundColor: getStatusColor(event.status)
                      }}
                      title={`${event.label} - ${formatDate(event.date)}`}
                    ></div>
                    
                    <div 
                      className="timeline-event-container"
                      style={{ 
                        left: `${leftPercentage}%`,
                        top: isAbove ? '10%' : '60%'
                      }}
                    >
                      <div 
                        className="timeline-status"
                        style={{ backgroundColor: getStatusColor(event.status) }}
                      >
                        <i className={`bi ${getStatusIcon(event.status)} me-1`}></i>
                        {event.status?.toUpperCase()}
                      </div>
                      
                      <div className="timeline-label">
                        {event.label}
                      </div>
                      
                      <div className="timeline-date">
                        {formatDate(event.date)}
                      </div>
                      
                      <div className="timeline-desc">
                        {event.desc}
                      </div>
                    </div>
                    
                    {index < timelineEvents.length - 1 && (
                      <div 
                        className="days-between"
                        style={{ left: `${leftPercentage + (70 / Math.max(timelineEvents.length - 1, 1)) / 2}%` }}
                      >
                        <i className="bi bi-calendar-date me-1"></i>
                        {calculateDaysBetween(event.date, timelineEvents[index + 1].date)}d
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              
              {/* Today marker */}
              {timelineEvents.length > 1 && (() => {
                const today = new Date();
                const startDate = new Date(timelineEvents[0]?.date);
                const endDate = new Date(timelineEvents[timelineEvents.length - 1]?.date);
                
                if (today >= startDate && today <= endDate) {
                  const totalDays = calculateDaysBetween(startDate.toISOString(), endDate.toISOString());
                  const daysPassed = calculateDaysBetween(startDate.toISOString(), today.toISOString());
                  const todayPercentage = 15 + (daysPassed / Math.max(totalDays, 1)) * 70;
                  
                  return (
                    <div 
                      className="timeline-today"
                      style={{ left: `${todayPercentage}%` }}
                    ></div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
        
        <div className="modal-footer p-3 border-top">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            <i className="bi bi-check-lg me-1"></i>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimelineModal;
